/**
 * Errors, said in a way the reader can act on.
 *
 * A thrown error carries two audiences. The person in front of the screen
 * needs to know whether to try again, fix something, or ask somebody; whoever
 * has to diagnose it needs the original text. Collapsing those into one
 * message serves neither — "NetworkError when attempting to fetch resource"
 * tells the reader nothing and looks like the application broke.
 */
export type ReadableError = {
  /** What to tell the person. Always a complete sentence. */
  message: string;
  /** The original, for a second line or the console. Absent when it adds nothing. */
  detail?: string;
};

/**
 * A failure with no response at all.
 *
 * The browsers word this differently and none of them mean anything to a
 * reader: Firefox says "NetworkError when attempting to fetch resource",
 * Chrome "Failed to fetch", Safari "Load failed". What they share is that the
 * request never landed, which is the one thing worth saying.
 */
function isConnectionFailure(text: string): boolean {
  return /networkerror|failed to fetch|load failed|network request failed|could not reach/i
    .test(text);
}

export function readableError(error: unknown, whenFailing: string): ReadableError {
  const raw = error instanceof Error ? error.message : String(error ?? "");

  if (isConnectionFailure(raw)) {
    return {
      message: `${whenFailing} because the connection failed. Please try again.`,
      detail: raw,
    };
  }

  // Anything the server refused has already been phrased for a person: those
  // messages are written in the mutations and say what is wrong.
  return { message: raw || `${whenFailing}.` };
}
